import { execFileSync } from 'node:child_process';
import { readFileSync } from 'node:fs';
import * as path from 'node:path';

import { getGitRepoRootSync } from '../../git/get-git-repo-root-sync.js';
import { resolveSafePathUnderCwd, toDisplayPathRelativeToCwd } from './safe-fs-path.js';

/**
 * Resolve an agent path (often absolute) to a filesystem path + stable display path.
 * Prefer paths relative to the git root when the file lies inside the repo so `git show HEAD:path` works from root.
 */
export function resolveWorkspaceFilePath(
  sessionParentPath: string,
  rawPath: string,
): { resolvedPath: string; display: string } | null {
  const trimmed = rawPath.trim();
  if (!trimmed) return null;

  const normalizedSessionParent = path.resolve(sessionParentPath);
  let resolvedPath = resolveSafePathUnderCwd(sessionParentPath, trimmed);

  if (!resolvedPath) {
    const candidate = path.isAbsolute(trimmed)
      ? path.normalize(trimmed)
      : path.normalize(path.resolve(normalizedSessionParent, trimmed));
    const gitRoot = getGitRepoRootSync(sessionParentPath);
    if (!gitRoot) return null;
    const rel = path.relative(gitRoot, candidate);
    if (rel.startsWith('..') || path.isAbsolute(rel)) return null;
    resolvedPath = candidate;
  }

  const gitRoot = getGitRepoRootSync(sessionParentPath);
  if (gitRoot) {
    const relFromRoot = path.relative(gitRoot, resolvedPath);
    if (!relFromRoot.startsWith('..') && !path.isAbsolute(relFromRoot)) {
      return { resolvedPath, display: relFromRoot.split(path.sep).join('/') };
    }
  }

  return { resolvedPath, display: toDisplayPathRelativeToCwd(sessionParentPath, resolvedPath) };
}

/** Read UTF-8 file content for a display path produced by resolveWorkspaceFilePath (git-root-relative preferred). */
export function readUtf8WorkspaceFile(sessionParentPath: string, displayPath: string): string {
  if (!displayPath || displayPath.includes('..')) return '';
  const gitRoot = getGitRepoRootSync(sessionParentPath);
  if (gitRoot) {
    const resolvedPath = path.resolve(gitRoot, displayPath);
    const rel = path.relative(gitRoot, resolvedPath);
    if (!rel.startsWith('..') && !path.isAbsolute(rel)) {
      try {
        return readFileSync(resolvedPath, 'utf8');
      } catch {
        /* fall through */
      }
    }
  }
  const resolvedPath = resolveSafePathUnderCwd(sessionParentPath, displayPath);
  if (!resolvedPath) return '';
  try {
    return readFileSync(resolvedPath, 'utf8');
  } catch {
    return '';
  }
}

/** `git show HEAD:<path>` where path is relative to repo root (same convention as resolveWorkspaceFilePath display). */
/** Resolve a git-root-relative (or cwd-relative) display path back to absolute for logging/diagnostics. */
export function tryWorkspaceDisplayToPath(sessionParentPath: string, displayPath: string): string | null {
  if (!displayPath || displayPath.includes('..')) return null;
  const gitRoot = getGitRepoRootSync(sessionParentPath);
  if (gitRoot) {
    const resolvedPath = path.resolve(gitRoot, displayPath);
    const rel = path.relative(gitRoot, resolvedPath);
    if (!rel.startsWith('..') && !path.isAbsolute(rel)) return resolvedPath;
  }
  return resolveSafePathUnderCwd(sessionParentPath, displayPath);
}

export function readGitHeadBlob(sessionParentPath: string, displayPath: string): string {
  if (!displayPath || displayPath.includes('..')) return '';
  const gitRoot = getGitRepoRootSync(sessionParentPath);
  const execCwd = gitRoot ?? sessionParentPath;
  try {
    return execFileSync('git', ['show', `HEAD:${displayPath}`], {
      cwd: execCwd,
      encoding: 'utf8',
      maxBuffer: 50 * 1024 * 1024,
    });
  } catch {
    return '';
  }
}
